import { useContext, useEffect, useMemo } from 'react';
import { WebSocketContext } from '../../providers/WebSocketProvider';
import { feedStatus } from '../../helpers/feedFreshness';

// Map 3D machine labels → Redis machine IDs
const MACHINE_MAP = {
  'l75': 'l75',
  'l120': 'l120',
  'l70': 'l70',
  'dtb1250': 'dtb1250',
  'skip2250': 'skip2250',
  'ach300': 'ach300',
  'dhc600': 'dhc600',
  'ath600': 'ath600'
};

const getSpeed = cps =>
  Number(
    cps?.LineSpeed ??
    cps?.LINE_SPEED ??
    cps?.LINE_SPEED_ACT ??
    cps?.ACT_LINE_SPEED ??
    cps?.ACT_SPEED ??
    cps?.ACT_RAMP_VALUE_PER ??
    0
  );

export default function useMachineData() {
  const ws = useContext(WebSocketContext);

  useEffect(() => {
    if (ws?.subscribeToChannel) {
      ws.subscribeToChannel('machine-all');
    }
    return () => {
      if (ws?.unsubscribeToChannel) ws.unsubscribeToChannel();
    };
  }, [ws?.socket]);

  const machines = useMemo(() => {
    const result = {};
    const states = ws?.machineData?.state || [];

    for (const m of states) {
      const id = m.machine;
      if (!id) continue;
      const cps = m.currentPlcState || {};
      const status = m.status?.label || 'inactive';
      const speed = getSpeed(cps);
      const mode = m.mode || cps.mode || '';
      const meterAct = Number(cps.METER_ACT ?? 0);
      const meterTotal = Number(m.total_meter_produced ?? cps.METER_TOTAL ?? 0);
      const operator = m.operator || '';

      // How old these tag values actually are.
      //
      // This adapter is the single chokepoint for the whole 3D scene, and it
      // already copied `plc: cps` while dropping the one field that says
      // whether any of it is current. A frozen l120 therefore pulsed a green
      // dot, spun its drum and counted towards the `LIVE` pill for two days.
      const feed = feedStatus(m);

      result[id] = {
        id,
        status,           // 'running' | 'inactive' | 'stopped'
        statusType: m.status?.type || 'warning',
        speed,            // m/min
        mode,             // 'PRODUCTION' | 'CLEAN UP' | 'UNWINDING' | 'UNKNOWN'
        meterAct,
        meterTotal,
        operator,
        plc: cps,         // raw PLC state for specific checks

        // Everything below this line is only true if `feedStale` is false.
        feedStale: feed.stale,
        feedAgeMs: feed.ageMs,
        feedLabel: feed.label,
        feedTitle: feed.title,

        // L75 specific
        capstanOn: !!cps.CapstanOnOff,
        e75On: !!cps.E75_ExtruderOnOff,
        e45On: !!cps.E45_ExtruderOnOff,
        takeup1Active: !!cps.TakeUp_1_StartLamp,
        takeup2Active: !!cps.TakeUp_2_StartLamp,

        // L120 specific
        catOn: !!cps.CAT_ON_OFF,
        e120On: !!cps.E_120_J_ON_OFF,

        // DTB1250 pay-off states (TO_NOT_START = true means blocked → active when false)
        payoffs: [1, 2, 3, 4, 5, 6, 7].map(n => ({
          num: n,
          active: !!cps[`PAY_OFF_${n}_TO_NOT_START`],
          emergency: !!cps[`PAY_OFF_EMG_${n}`],
          selected: !!cps[`PAY_OFF_${n}_SELEC`]
        })),

        // Skip2250
        tthRpm: Number(cps.TTH1_RPM ?? 0),
        capstanRpm: Number(cps.CAPSTAN_MOTOR_RPM ?? 0),

        // ACH300
        windingRpm: Number(cps.WINDING_ACT_RPM ?? 0),
        traversePos: Number(cps.TRAVER_ACT_POS ?? 0),

        // Running flag
        // A frozen feed is not a running machine. This one flag drives the
        // pulsing status dot, the spinning drum and the `LIVE` count, all of
        // which insisted l120 was fine while its link was dead.
        isRunning: status === 'running' && !feed.stale
      };
    }

    return result;
  }, [ws?.machineData]);

  return machines;
}
